import React, { useMemo, useRef } from "react";
import {
  alpha,
  AppBar,
  Box,
  CardMedia,
  Dialog,
  Stack,
  Toolbar,
  Typography,
} from "@mui/material";
import Logo from "../../../../../../../assets/MainLogo/Imagotipo_EG.svg";
import { parseDateToLgComun3 } from "../../../../../../../helpers/dateTransform";
import { sanitizeForInnerHTML } from "../../../../../../../helpers/sanitizeHtml";
import { EGButton } from "../../../../../../MainComponents/Button/EGButton/EGButton";
import { EGIconButton } from "../../../../../../MainComponents/Button/IconButton/IconButton";

export const PrintInterview = ({
  interview,
  show,
  onClose,
  t,
  theme,
  getUserLabel,
  getUsersWitness,
  rolesOpts,
  typeLabel,
  pointsToEvaluateLabels,
  providerLabel,
  interviewed,
}) => {
  const evaluationPoints = useMemo(() => {
    if (!interview?.evaluation?.point) return [];
    let points = Object.entries(interview.evaluation.points).map(
      ([key, value]) => ({
        key,
        value,
      })
    );
    points = points.filter((point) => point.value?.value);
    return points;
  }, [interview.evaluation]);

  const printableRef = useRef(null);

  const handlePrint = () => {
    // Llamamos directamente a la ventana de impresión del navegador
    window.print();
  };

  return (
    <>
      <Dialog
        open={show}
        fullWidth
        maxWidth="md"
        onClose={onClose}
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        {/* App Bar Actions */}
        <AppBar
          sx={{
            position: "sticky",
            backgroundColor: theme.palette.blueGray200.main,
            "@media print": {
              display: "none !important",
            },
          }}
        >
          <Toolbar
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Stack spacing={4} direction="row">
              <EGButton
                label="Imprimir"
                onClick={handlePrint}
                iconName="printer"
              />
            </Stack>
            <EGIconButton
              iconName="close_icon"
              iconColor="black"
              onClick={onClose}
              iconSize={25}
            />
          </Toolbar>
        </AppBar>
        {/* Content */}
        <Box
          ref={printableRef}
          p={{ xs: 2, md: 4 }}
          sx={{ minHeight: "50vh", overflowY: "auto" }}
        >
          {/* Logo */}
          <Box width={"100%"}>
            <CardMedia
              component="img"
              image={Logo}
              loading="lazy"
              alt={"Logo"}
              sx={{
                width: "auto",
                height: "35px",
                maxHeight: "80vh",
                objectFit: "contain",
              }}
            />
          </Box>
          {/* Interview date */}
          {interview?.scheduled?.trim() && (
            <Box mt={1}>
              <Typography fontSize={13} textAlign="end" mb={1}>
                <Box component="span" color="blueGray900" fontWeight={600}>
                  {`${t("Interview.interview_date")}: `}
                </Box>
                <Box component="span" color="primary.main" fontWeight={700}>
                  {parseDateToLgComun3(interview.scheduled, undefined, undefined, false)}
                </Box>
              </Typography>
            </Box>
          )}
          {/* Interviewee */}
          <Typography fontSize={13} textAlign="start" mt={1.5}>
            <Box component="span" color="blueGray900" fontWeight={600}>
              {`${t("Interview.interviewee")}: `}
            </Box>
            <Box component="span" fontWeight={500}>
              {getUsersWitness(interviewed)}
            </Box>
          </Typography>
          {/* Interviewer */}
          <Typography fontSize={13} textAlign="start" mt={0.5}>
            <Box component="span" color="blueGray900" fontWeight={600}>
              {`${t("Interview.interviewer")}: `}
            </Box>
            <Box component="span" fontWeight={500}>
              {getUsersWitness(interview.interviewer)}
            </Box>
          </Typography>
          {/* Participants */}
          {interview.witness.length > 0 &&
            interview.witness.map((participant, idx) => (
              <Typography fontSize={13} textAlign="start" mt={0.5} key={idx}>
                <Box component="span" color="blueGray900" fontWeight={600}>
                  {`${
                    rolesOpts.find((opt) => opt.value === participant.role)
                      ?.label
                  }: `}
                </Box>
                <Box component="span" fontWeight={500}>
                  {getUserLabel(participant)}
                </Box>
              </Typography>
            ))}
          {/* Objective of the interview */}
          {interview?.details?.trim() && (
            <Box sx={{ mt: 1.5 }}>
              <Typography
                fontSize={14}
                fontWeight={600}
                py={1}
                borderTop={`1px solid ${theme.palette.blueGray200.main}`}
              >
                {t("Interview.interview_objectives")}:
              </Typography>
              <Typography
                fontSize={13}
                textAlign="justify"
                sx={{ textIndent: "1.5em", whiteSpace: "pre-line", px: 1 }}
              >
                {interview.details}
              </Typography>
            </Box>
          )}
          {/* Meeting details */}
          <Box mt={1.5}>
            <Typography
              fontSize={14}
              fontWeight={600}
              pt={1}
              borderTop={`1px solid ${theme.palette.blueGray200.main}`}
            >
              {`${t("Interview.interview")} ${typeLabel} ${
                providerLabel && ` (${providerLabel}):`
              }`}
            </Typography>
            {interview?.kindDetails?.trim() && (
              <Typography
                fontSize={13}
                textAlign="justify"
                sx={{
                  overflowWrap: "anywhere",
                  whiteSpace: "pre-line",
                  textIndent: "1.5em",
                  px: 1,
                  mt: 1,
                }}
              >
                {interview.kindDetails}
              </Typography>
            )}
          </Box>
          {/* Interview */}
          {interview.interview.length > 0 && (
            <Box mt={1.5}>
              <Typography
                color="primary"
                variant="h5"
                py={1}
                borderTop={`1px solid ${theme.palette.blueGray200.main}`}
              >
                {t("Interview.interview")}
              </Typography>
              {interview.interview.map((item, index) => (
                <Stack key={index} spacing={1} sx={{ pb: 1.5, px: 1 }}>
                  {/* # Question */}
                  <Typography fontSize={13} fontWeight={600}>
                    {`${index + 1}. ${item.question}`}
                  </Typography>
                  {/* Question Objective */}
                  {item?.details && (
                    <Typography
                      textAlign="justify"
                      sx={{ whiteSpace: "pre-wrap" }}
                      fontSize={13}
                    >
                      {`${t("Interview.objective")}: ${item.details}`}
                    </Typography>
                  )}
                  {/* Interview answer */}
                  {item?.answer ? (
                    <Typography
                      sx={{
                        py: 0.75,
                        px: 1,
                        borderRadius: 2,
                        whiteSpace: "pre-wrap",
                        background: alpha(theme.palette.blueGray50.main, 0.75),
                      }}
                      textAlign="justify"
                      fontStyle="italic"
                      fontSize={13}
                    >
                      — {item.answer}
                    </Typography>
                  ) : (
                    <Typography
                      sx={{ fontStyle: "italic", color: "secondary.light" }}
                      fontSize={12}
                      px={1}
                    >
                      {t("Interview.no_response_recorded")}
                    </Typography>
                  )}
                  {/* Aditional question details */}
                  {item?.answerDetails &&
                    item?.answerDetails?.trim() !== "" && (
                      <Typography
                        variant="body2"
                        lineHeight={1.4}
                        textAlign="justify"
                        fontSize={13}
                        sx={{
                          borderLeft: "4px solid",
                          borderColor: "grey.200",
                          display: "block",
                          p: 0.5,
                          borderRadius: 1,
                        }}
                      >
                        <Typography
                          fontWeight={600}
                          component="span"
                          fontSize={13}
                        >
                          {t("evaluation.additional_details")}:
                        </Typography>{" "}
                        {item.answerDetails}
                      </Typography>
                    )}
                </Stack>
              ))}
            </Box>
          )}
          {/* Evaluation */}
          {evaluationPoints.length > 0 && (
            <>
              <Typography
                color="primary"
                variant="h5"
                py={1}
                borderTop={`1px solid ${theme.palette.blueGray200.main}`}
              >
                {t("Interview.interview_evaluation")}
              </Typography>
              {evaluationPoints.map(({ key, value }, index) => (
                <Box key={index} mb={1}>
                  <Typography
                    fontSize={13}
                    component="li"
                    fontWeight={600}
                    ml={2.5}
                  >
                    {`${pointsToEvaluateLabels[key] || key}`}
                  </Typography>
                  <Typography
                    sx={{ color: "text.secondary", py: 0.33, px: 2 }}
                    fontSize={13}
                    fontWeight={600}
                  >
                    – {t(`evaluation.${[value.value]}`)}
                  </Typography>
                  {value?.details && value?.details?.trim() !== "" && (
                    <Typography
                      lineHeight={1.4}
                      textAlign="justify"
                      fontSize={13}
                      sx={{
                        my: 0.5,
                        ml: 1.5,
                        mb: 1,
                        borderLeft: "4px solid",
                        borderColor: "grey.200",
                        display: "block",
                        p: 0.5,
                        borderRadius: 1,
                        whiteSpace: "pre-wrap",
                      }}
                    >
                      <Typography
                        fontWeight={600}
                        component="span"
                        fontSize={13}
                      >
                        {t("evaluation.specific_comment")}:
                      </Typography>{" "}
                      {value.details}
                    </Typography>
                  )}
                </Box>
              ))}
            </>
          )}
          {/* Evaluation aditional details */}
          {interview?.evaluation?.details &&
            interview?.evaluation?.details?.trim() !== "" && (
              <Box my={1.5}>
                <Typography
                  color="primary"
                  variant="h5"
                  py={1}
                  borderTop={`1px solid ${theme.palette.blueGray200.main}`}
                >
                  {t("evaluation.general_evaluation_comments")}
                </Typography>
                <Typography
                  fontSize={13}
                  textAlign="justify"
                  sx={{ textIndent: "1.5em", whiteSpace: "pre-line", px: 1 }}
                >
                  {interview.evaluation.details}
                </Typography>
              </Box>
            )}
          {/* Conclusion */}
          {interview?.conclusion && (
            <Box my={1.5}>
              <Typography
                color="primary"
                variant="h5"
                py={1}
                borderTop={`1px solid ${theme.palette.blueGray200.main}`}
              >
                {t("Interview.conclusion")}
              </Typography>
              <Typography component="div" px={1}>
                <div
                  dangerouslySetInnerHTML={{
                    __html: sanitizeForInnerHTML(interview.conclusion),
                  }}
                />
              </Typography>
            </Box>
          )}
        </Box>
      </Dialog>
    </>
  );
};
